// © Microsoft Corporation. All rights reserved.

import { ExceptionCallingAuthenticatorApi, logException } from "@msnews/diagnostics";
import { IWidgetAuthenticator, IWidgetInstance, widgetApiVersion } from "@msnews/core";

/**
 * Returns the account type of the user ie. "consumers" or "organizations"
 * @param widgetInstance The current widget's instance
 */
export async function getWindowsWidgetAccountType(widgetInstance: IWidgetInstance): Promise<string> {
    if (!widgetInstance) {
        return null;
    }

    try {
        const authenticatorApi = widgetInstance.tryGetApi<IWidgetAuthenticator>("authenticator", widgetApiVersion, widgetApiVersion);
        const accountType = await authenticatorApi.getProperty("ProviderAuthority");
        return accountType;
    } catch (error) {
        logException(error, ExceptionCallingAuthenticatorApi, "Exception fetching ProviderAuthority", JSON.stringify(error));
        return null;
    }
}

/**
 * Fetches Windows Widget token for requested params
 * @param widgetInstance The current widget's instance
 * @param clientId The client id of the AAD application requesting access
 * @param scope The scope to request access for
 * @param resource The resource to request access for
 * @param apiVersion
 */
export async function getWindowsWidgetToken(widgetInstance: IWidgetInstance, clientId: string, scope: string, resource: string, apiVersion = ""): Promise<string> {
    if (!widgetInstance) {
        return null;
    }

    try {
        const authenticatorApi = widgetInstance.tryGetApi<IWidgetAuthenticator>("authenticator", widgetApiVersion, widgetApiVersion);
        const token = await authenticatorApi.getToken(clientId, scope, resource, apiVersion);
        return token;
    } catch (error) {
        logException(error, ExceptionCallingAuthenticatorApi, "Exception calling GetToken", JSON.stringify(error));
        return null;
    }
}

/**
 * Fetches MUID from Windows
 * @param widgetInstance The current widget's instance
 * @returns MUID value
 */
export async function getWindowsWidgetMuid(widgetInstance: IWidgetInstance): Promise<string> {
    if (!widgetInstance) {
        return null;
    }

    try {
        const authenticatorApi = widgetInstance.tryGetApi<IWidgetAuthenticator>("authenticator", widgetApiVersion, widgetApiVersion);
        const muid = await authenticatorApi.getProperty("MUID");
        return muid;
    } catch (error) {
        logException(error, ExceptionCallingAuthenticatorApi, "Exception fetching MUID", JSON.stringify(error));
        return null;
    }
}